import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    List,
    ListItemButton,
    ListItemText,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faServer } from '@fortawesome/pro-solid-svg-icons';
import { useApplicationContext } from 'ApplicationContext';

/**
 * Eine Gruppe aus dem Active Directory. Lokal getippt, weil der Endpoint im generierten
 * Client noch `any` liefert (das 200er-Schema fehlt in der Swagger-Definition).
 */
export interface LdapGroupRow {
    cn?: string;
    dn?: string;
    description?: string | null;
    linkedPermissionGroups?: string[] | null;
}

interface Props {
    open: boolean;
    onClose: () => void;
    /** Wird mit dem gewählten CN aufgerufen. */
    onPick: (cn: string) => void;
}

/**
 * Auswahl einer AD-Gruppe aus dem Verzeichnis, damit CNs nicht getippt werden müssen — ein
 * Tippfehler würde sonst nur als stumme Login-Ablehnung auffallen.
 *
 * Die Anmeldedaten werden nur für die Suche an den Server geschickt und dort nicht gespeichert;
 * im Standardbetrieb existiert kein Service-Account, mit dem der Server selbst suchen könnte.
 */
export const LdapGroupPickerDialog: React.FC<Props> = ({ open, onClose, onPick }) => {
    const { api } = useApplicationContext();
    const [upn, setUpn] = useState('');
    const [password, setPassword] = useState('');
    const [search, setSearch] = useState('');
    const [rows, setRows] = useState<LdapGroupRow[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /** null = noch nicht ermittelt. true = der Server kann selbst suchen, es sind keine Eingaben nötig. */
    const [hasServiceAccount, setHasServiceAccount] = useState<boolean | null>(null);

    const load = useCallback(
        async (useServiceAccount: boolean) => {
            setLoading(true);
            setError(null);
            try {
                // Ist ein Service-Account konfiguriert, sucht der Server damit — dann fließt kein
                // persönliches AD-Passwort durch die Anwendung.
                const credentials = useServiceAccount
                    ? { upn: null, password: null }
                    : { upn: upn.trim() || null, password: password || null };
                const response = await api.ldapAdmin.ldapAdminListGroups(credentials, {
                    search: search.trim() || null,
                });
                setRows((response.data as unknown as LdapGroupRow[]) ?? []);
            } catch (e: any) {
                // Der Server liefert bei fehlgeschlagenem Bind eine Klartext-Meldung als 400.
                const detail = e?.response?.data?.detail ?? e?.response?.data?.title ?? e?.response?.data;
                setError(typeof detail === 'string' ? detail : 'Die Gruppen konnten nicht geladen werden.');
                setRows(null);
            } finally {
                setLoading(false);
            }
        },
        [api, upn, password, search],
    );

    // Beim Öffnen klären, ob der Server einen Service-Account hat. Wenn ja, direkt suchen.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        api.ldapAdmin
            .ldapAdminGetSettings()
            .then((r) => {
                if (cancelled) return;
                const available = r.data.serviceAccountPasswordConfigured ?? false;
                setHasServiceAccount(available);
                if (available) void load(true);
            })
            .catch(() => !cancelled && setHasServiceAccount(false));
        return () => {
            cancelled = true;
        };
        // Absichtlich nur an `open` gekoppelt: `load` ändert sich mit jedem Tastendruck im Suchfeld.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, api]);

    const pick = (cn?: string) => {
        if (!cn) return;
        onPick(cn);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <FontAwesomeIcon icon={faServer} />
                    <span>AD-Gruppe auswählen</span>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {hasServiceAccount === false && (
                        <>
                            <Alert severity="info">
                                Es ist kein Service-Account konfiguriert, daher wird für die Suche eine persönliche
                                Anmeldung benötigt. Die Daten werden nur für diese Suche verwendet und nicht
                                gespeichert. Mit Service-Account entfällt diese Eingabe.
                            </Alert>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Anmeldename"
                                    placeholder="vorname.nachname@firma.local"
                                    value={upn}
                                    onChange={(e) => setUpn(e.target.value)}
                                />
                                <TextField
                                    size="small"
                                    fullWidth
                                    type="password"
                                    label="Passwort"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </Stack>
                        </>
                    )}
                    {hasServiceAccount === true && (
                        <Typography variant="body2" color="text.secondary">
                            Gesucht wird über den konfigurierten Service-Account — es ist keine persönliche Anmeldung
                            nötig.
                        </Typography>
                    )}
                    <Stack direction="row" spacing={1}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Namensfilter (optional)"
                            placeholder="Support"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') void load(hasServiceAccount === true);
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={() => void load(hasServiceAccount === true)}
                            disabled={loading || (hasServiceAccount === false && (!upn.trim() || !password))}
                        >
                            Suchen
                        </Button>
                    </Stack>

                    {loading && <LinearProgress />}
                    {error && <Alert severity="error">{error}</Alert>}

                    {rows && rows.length === 0 && !loading && (
                        <Alert severity="info">Keine Gruppen gefunden. Filter weiter fassen oder leer lassen.</Alert>
                    )}

                    {rows && rows.length > 0 && (
                        <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                            <List dense>
                                {rows.map((g) => (
                                    <ListItemButton key={g.dn || g.cn} onClick={() => pick(g.cn)}>
                                        <ListItemText
                                            primary={
                                                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                                    <span>{g.cn}</span>
                                                    {(g.linkedPermissionGroups?.length ?? 0) > 0 && (
                                                        <Chip
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                            label={`verknüpft: ${g.linkedPermissionGroups!.join(', ')}`}
                                                        />
                                                    )}
                                                </Stack>
                                            }
                                            secondary={g.description || g.dn}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Abbrechen</Button>
            </DialogActions>
        </Dialog>
    );
};

export default LdapGroupPickerDialog;
